import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import type { Icon } from "@phosphor-icons/react";
import { HoloRings } from "./HoloRings";
import { callout, layoutRing, type RingKind } from "./ringLayout";

// The center of the home scene: rings, the things on them (real cameras,
// buttons and drones as nodes with leader-line callouts), the badge, and the
// radial menu the badge fans out. Positions are schematic (ringLayout.ts).

export type NodeTone = "normal" | "hot" | "warn" | "off" | "armed";

export interface RingNode {
  id: string;
  kind: RingKind;
  name: string;
  sub: string;
  tone: NodeTone;
  /** Screen-reader name, e.g. "North Gate button, pressed 9:15. Open alerts." */
  label: string;
}

export interface RadialItem {
  key: string;
  label: string;
  icon: Icon;
  onSelect: () => void;
}

const MARK_CLASS: Record<NodeTone, string> = { normal: "", hot: " hot", warn: " warn", off: " off", armed: " hot" };

function clip(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

export function Hologram({
  nodes,
  selectedId,
  onNode,
  radial,
  menuOpen,
  setMenuOpen,
  threat,
}: {
  nodes: { cam: RingNode[]; btn: RingNode[]; drone: RingNode[] };
  selectedId: string | null;
  onNode: (n: RingNode) => void;
  radial: RadialItem[];
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  threat: boolean;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [px, setPx] = useState(560);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setPx(el.getBoundingClientRect().width || 560);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = px / 560;
  const compact = px < 420;
  const total = nodes.cam.length + nodes.btn.length + nodes.drone.length;
  const crowded = total > 12;
  // Readable callouts: never under 10 px on screen.
  const fs = Math.max(12, 10 / scale);
  const hitR = 22 / scale;
  const placed = layoutRing(nodes);

  // Close the radial menu on outside press or Escape; focus its first item on open.
  useEffect(() => {
    if (!menuOpen) return;
    menuRef.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || badgeRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [menuOpen, setMenuOpen]);

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen(false);
      badgeRef.current?.focus();
    } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      items[(i + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      items[(i - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Tab") {
      setMenuOpen(false);
    }
  };

  // The mockup puts menu items 150 px out; on small rings keep them clear of the badge.
  const R = Math.max(150 * scale, 88);

  return (
    <div className={`hologram${threat ? " hologram--threat" : ""}`} ref={boxRef}>
      <HoloRings variant={threat ? "threat" : "home"} idPrefix="home" />
      <svg className="hologram__nodes" viewBox="0 0 560 560" role="group" aria-label="Cameras, buttons and drones">
        {placed.map((p) => {
          const n = p.item;
          const sel = n.kind === "cam" && n.id === selectedId;
          const co = callout(p.angle, p.r, compact);
          const { x, y } = p;
          const showSub = !compact && (!crowded || sel || n.tone === "hot");
          const name = clip(n.name.toLowerCase(), compact || crowded ? 12 : 18);
          const activate = () => onNode(n);
          return (
            <g
              key={`${n.kind}-${n.id}`}
              className={`node node--${n.kind}${sel ? " sel" : ""}${n.tone === "hot" || n.tone === "armed" ? " node--hot" : ""}`}
              data-node-sel={sel ? "1" : undefined}
              tabIndex={0}
              role="button"
              aria-label={n.label}
              aria-pressed={n.kind === "cam" ? sel : undefined}
              onClick={activate}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  activate();
                }
              }}
            >
              <path className="lead" d={`M${x.toFixed(1)} ${y.toFixed(1)} L${co.lx.toFixed(1)} ${co.ly.toFixed(1)} L${co.ex.toFixed(1)} ${co.ly.toFixed(1)}`} />
              {sel ? <circle className="selring" cx={x} cy={y} r={11} /> : null}
              {n.kind === "cam" ? <rect className={`mark${MARK_CLASS[n.tone]}`} x={x - 5} y={y - 5} width={10} height={10} /> : null}
              {n.kind === "btn" ? <circle className={`mark${MARK_CLASS[n.tone]}`} cx={x} cy={y} r={5} /> : null}
              {n.kind === "drone" ? <path className={`mark${MARK_CLASS[n.tone]}`} d={`M${x} ${y - 7} L${x + 6} ${y + 5} L${x - 6} ${y + 5} Z`} /> : null}
              <text x={co.tx} y={showSub ? co.ly - 3 : co.ly + 4} textAnchor={co.anchor} fontSize={fs} className="node__name">
                {name}
              </text>
              {showSub ? (
                <text x={co.tx} y={co.ly + 12 * (fs / 12)} textAnchor={co.anchor} fontSize={fs * 0.92} className={`node__sub${n.tone === "hot" || n.tone === "armed" ? " node__sub--hot" : ""}`}>
                  {n.sub}
                </text>
              ) : null}
              <circle className="hit" cx={x} cy={y} r={hitR} />
              <circle className="focusring" cx={x} cy={y} r={hitR * 0.72} vectorEffect="non-scaling-stroke" />
            </g>
          );
        })}
      </svg>
      <button
        type="button"
        ref={badgeRef}
        className="badge"
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls="radial-menu"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <img src="/brand/logo-640.webp" srcSet="/brand/logo-320.webp 320w, /brand/logo-640.webp 640w" sizes="160px" alt="" width={160} height={160} decoding="async" />
      </button>
      <div id="radial-menu" className={`radial${menuOpen ? " open" : ""}`} role="menu" aria-label="Sections" ref={menuRef} onKeyDown={onMenuKey}>
        {radial.map((item, i) => {
          const a = ((-90 + (i * 360) / radial.length) * Math.PI) / 180;
          return (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className="btn radial__item"
              tabIndex={menuOpen ? 0 : -1}
              aria-hidden={menuOpen ? undefined : true}
              style={{ ["--x" as string]: `${(Math.cos(a) * R).toFixed(1)}px`, ["--y" as string]: `${(Math.sin(a) * R).toFixed(1)}px` }}
              onClick={() => {
                setMenuOpen(false);
                item.onSelect();
              }}
            >
              <item.icon className="btn__icon" size={18} weight="light" aria-hidden="true" />
              <span className="btn__label">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
